import type { ThunkAction } from "redux-thunk";

import { Link } from "../../../../../types/link";
import {
  Certificate,
  ChildRegistration,
  CompletePendingRegistration,
  CompleteRegistration,
  IntegrationPartner,
  OrganisationDetails,
  OrganisationRegistrant,
  RegisteredOrganisation,
  RegistrableOrganisation,
} from "../../../../../types/registration";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../../../analytics/SiteImprove";
import { getJSON, post } from "../../digipost-fetch";
import { getLanguageFromPath } from "../../LanguageContext";
import { findURIWithWindowHostname } from "../../linkUtils";
import { logError } from "../../serverlog";
import { isBehandlerFlow } from "../../stateAccessors";
import { fetchEntrypointAction } from "../app/app-actions";
import { ReduxState } from "../state";
import { ThunkContext } from "../thunkContext";

export enum RegistrationStatus {
  NONE = "NONE",
  SUBMITTING = "SUBMITTING",
  COMPLETE = "COMPLETE",
  FAILED = "FAILED",
  ORGANISATION_ALREADY_INTEGRATED = "ORGANISATION_ALREADY_INTEGRATED",
  REGISTRANT_TOO_YOUNG = "REGISTRANT_TOO_YOUNG",
  REGISTRANT_MISSES_NAME = "REGISTRANT_MISSES_NAME",
  TECHNICAL_FAIL = "TECHNICAL_FAIL",
}

export type UserInput = {
  selectedIntegrationPartner?: IntegrationPartner;
  selectedOrganisationNumber: string;
  mailbox: boolean;
  showPartnerChoice: boolean;
  email: string;
  phone: string;
};

export type UserInputAction = {
  type: "SET_USER_INPUT";
  userInput: Partial<UserInput>;
};

export function setUserInputAction(
  userInput: Partial<UserInput>,
): UserInputAction {
  return {
    type: "SET_USER_INPUT",
    userInput,
  };
}

type OrganisationsFetchedAction = {
  type: "ORGANISATIONS_FETCHED";
  org: OrganisationRegistrant;
  behandlerFlow: boolean;
  selectedIntegrationPartner?: IntegrationPartner;
  preselectedOrg?: RegistrableOrganisation;
};

export function organisationsFetched(
  org: OrganisationRegistrant,
  behandlerFlow: boolean,
  partnerId?: string,
  preselectedOrg?: string,
): OrganisationsFetchedAction {
  return {
    type: "ORGANISATIONS_FETCHED",
    org,
    behandlerFlow,
    selectedIntegrationPartner: org.integrationPartners.find(
      (i) => partnerId !== undefined && i.senderId === parseInt(partnerId),
    ),
    preselectedOrg: preselectedOrg
      ? org.registrableOrganisations.find(
          (i) => i.organisationNumber === preselectedOrg,
        ) || org.registrableOrganisations[0]
      : org.registrableOrganisations.length === 1
        ? org.registrableOrganisations[0]
        : undefined,
  };
}

export type RegistrationStatusAction = {
  type: "SET_REGISTRATION_STATUS";
  registrationStatus: RegistrationStatus;
};

export function setRegistrationStatus(
  registrationStatus: RegistrationStatus,
): RegistrationStatusAction {
  return {
    type: "SET_REGISTRATION_STATUS",
    registrationStatus,
  };
}

export type CertificateFetchedAction = {
  type: "CERTIFICATE_FETCHED";
  certificate: Certificate;
};

export function certificateFetched(
  certificate: Certificate,
): CertificateFetchedAction {
  return {
    type: "CERTIFICATE_FETCHED",
    certificate,
  };
}

type IntegrationPartnerRegisteredAction = {
  type: "INTEGRATION_PARTNER_REGISTERED";
  digipostaddress: string;
  senderId: string;
  organisationNumber: string;
  existing: boolean;
  link: Array<Link>;
};

export function integrationPartnerRegistered(
  digipostadress: string,
  senderid: string,
  organisationNumber: string,
  existing: boolean,
  link: Array<Link>,
): IntegrationPartnerRegisteredAction {
  return {
    type: "INTEGRATION_PARTNER_REGISTERED",
    digipostaddress: digipostadress,
    senderId: senderid,
    organisationNumber: organisationNumber,
    existing: existing,
    link: link,
  };
}

export function organisationAction(): ThunkAction<
  Promise<void>,
  ReduxState,
  never,
  RegistrationStatusAction | OrganisationsFetchedAction
> {
  return async (dispatch, getState) => {
    const uri = findURIWithWindowHostname(
      "register_organisation",
      getState().link,
    );

    const data = await getJSON<OrganisationRegistrant>(uri);

    const behandlerFlow = isBehandlerFlow(getState());

    // Vi validerer teknisk oppsett før vi i det hele tatt dispatcher at vi har organisasjoner:
    if (behandlerFlow) {
      const redirectUrlParam = getState().app.params.redirectUrl;
      const integrationPartner = data.integrationPartners.find((i) => {
        const partnerId = getState().app.params.partnerId;
        return partnerId !== undefined && i.senderId === parseInt(partnerId);
      });
      if (!integrationPartner) {
        dispatch(setRegistrationStatus(RegistrationStatus.TECHNICAL_FAIL));
      }
      const domain = integrationPartner?.domain;
      if (
        redirectUrlParam &&
        domain &&
        !redirectUrlParam.startsWith(domain) &&
        !domain.startsWith(redirectUrlParam)
      ) {
        dispatch(setRegistrationStatus(RegistrationStatus.TECHNICAL_FAIL));
        return;
      }
    }

    const partnerid = behandlerFlow
      ? getState().app.params.partnerId
      : undefined;
    const preselectedOrg = getState().app.params.preselectedOrg;

    dispatch(
      organisationsFetched(data, behandlerFlow, partnerid, preselectedOrg),
    );
  };
}

export function organitionPendingAction(
  formData: CompletePendingRegistration,
): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  RegistrationStatusAction
> {
  const language = getLanguageFromPath();
  return async (dispatch, getState, { navigate }) => {
    const { app, link } = getState();

    const uri = findURIWithWindowHostname(
      "register_organisation_pending",
      link,
    );
    try {
      const response = await post(uri, formData, app.csrfToken);
      if (response.status === 202) {
        dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));
        navigate(`/registrering/${language}/bedrift/manuell_confirm`);
      } else if (response.status === 409) {
        const body = await response.json();
        if (body["error-code"] === "INTEGRATION_ALREADY_EXISTS_FOR_PARTNER") {
          dispatch(
            setRegistrationStatus(
              RegistrationStatus.ORGANISATION_ALREADY_INTEGRATED,
            ),
          );
        } else {
          dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
        }
      } else {
        dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      }
    } catch (e) {
      dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      logError(e as Error);
    }
  };
}

export function organisationChildAction(
  formData: ChildRegistration,
  organisationLinks: Array<Link>,
  onCreated: (newAccountId: string) => void,
): ThunkAction<Promise<void>, ReduxState, never, RegistrationStatusAction> {
  return async (dispatch, getState) => {
    const { app } = getState();

    const uri = findURIWithWindowHostname(
      "organisation_children",
      organisationLinks,
    );
    try {
      //Don't redirect, as we show error-message
      const response = await post(uri, formData, app.csrfToken, "#");
      if (response.status === 202) {
        siteImproveEvent({
          category: SiteImproveCategories.UNDERKONTO,
          action: SiteImproveActions.UNDERKONTO_SENDT,
        });
        window.location.href = `/virksomhetsadmin/konto/underkontoer?til_behandling=true`;
      } else if (response.status === 204) {
        //DIFI registration
        const location = response.headers.get("Location") as string;
        const newAccountId = location.split("/").pop() as string;

        siteImproveEvent({
          category: SiteImproveCategories.UNDERKONTO,
          action: SiteImproveActions.UNDERKONTO_OFFENTLIG,
        });

        onCreated(newAccountId);

        window.location.href = "/virksomhetsadmin/konto/underkontoer";
      } else {
        dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      }
    } catch (e) {
      dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      logError(e as Error);
    }
  };
}

function createBody(
  userInput: UserInput,
  integrationType?: string,
  signeringflow?: boolean,
): CompleteRegistration {
  const language = getLanguageFromPath();
  return {
    email: userInput.email,
    telephoneNumber: userInput.phone,
    organisationNumber: userInput.selectedOrganisationNumber,
    integrationPartnerId: userInput.selectedIntegrationPartner
      ? userInput.selectedIntegrationPartner.senderId
      : undefined,
    integrationType,
    language: language,
    activateMailbox: userInput.mailbox,
    registrationContext: signeringflow ? "signering" : "digipost",
  };
}

export function completeRegistrationAction(
  signeringflow: boolean,
  behandlerFlow: boolean,
  integrationType?: string,
): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  | RegistrationStatusAction
  | IntegrationPartnerRegisteredAction
  | CertificateFetchedAction
> {
  const language = getLanguageFromPath();
  return async (dispatch, getStore, context) => {
    try {
      const { app, org, link } = getStore();
      const uri = findURIWithWindowHostname("complete_registration", link);
      const body = createBody(org.userInput, integrationType, signeringflow);
      dispatch(setRegistrationStatus(RegistrationStatus.SUBMITTING));

      const response = await post(uri, body, app.csrfToken);

      if (response.status === 204) {
        if (signeringflow) {
          dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));
          context.navigate(`/registrering/${language}/bedrift/sconfirm`);
        } else if (behandlerFlow) {
          // TODO: fjern så snart digipost-backend er over på å returnere 201 istedenfor 204
          const location = response.headers.get("Location");
          const orgRegistered: OrganisationDetails = await getJSON(
            location as string,
          );
          dispatch(
            integrationPartnerRegistered(
              orgRegistered.digipostaddress,
              orgRegistered.senderId,
              orgRegistered.organisationNumber,
              true,
              orgRegistered.link,
            ),
          );
          if (integrationType === "2") {
            await fetchEntrypointAction()(dispatch, getStore, context); // Brukerens session blir oppgradert til Virksomhet og vi må sørge for å oppdatere links i redux

            const certurl = findURIWithWindowHostname(
              "rest_certificate",
              orgRegistered.link,
            );

            const certificate = await getJSON<Certificate>(certurl);

            dispatch(certificateFetched(certificate));
            dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));

            context.navigate(`/registrering/${language}/bedrift/i2confirm`);
          } else {
            context.navigate(`/registrering/${language}/bedrift/iconfirm`);
          }
        } else {
          // TODO: fjern så snart digipost-backend er over på å returnere 201 istedenfor 204
          siteImproveEvent({
            category: SiteImproveCategories.ORGANISATION_DONE,
            action: SiteImproveActions.ORGANISATION_DONE_TO_DIGIPOST,
          });

          const registeredOrganisation: RegisteredOrganisation =
            await response.json();
          context.navigate(
            `/registrering/${language}/bedrift/completed?senderId=${registeredOrganisation.registeredSenderId}`,
          );
        }
      } else if (response.status === 201) {
        if (signeringflow) {
          dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));
          context.navigate(`/registrering/${language}/bedrift/sconfirm`);
        } else if (behandlerFlow) {
          const registeredOrganisation: RegisteredOrganisation =
            await response.json();
          const location = response.headers.get("Location") as string;
          const orgRegistered: OrganisationDetails = await getJSON(location);
          dispatch(
            integrationPartnerRegistered(
              registeredOrganisation.integrationPartnerRegistered &&
                registeredOrganisation.registeredTechnicalAccountDigipostAddress
                ? registeredOrganisation.registeredTechnicalAccountDigipostAddress
                : registeredOrganisation.registeredDigipostAddress,
              registeredOrganisation.integrationPartnerRegistered &&
                registeredOrganisation.registeredTechnicalAccountSenderId
                ? registeredOrganisation.registeredTechnicalAccountSenderId
                : registeredOrganisation.registeredSenderId,
              registeredOrganisation.organisationNumber,
              true,
              orgRegistered.link,
            ),
          );
          if (integrationType === "2") {
            await fetchEntrypointAction()(dispatch, getStore, context); // Brukerens session blir oppgradert til Virksomhet og vi må sørge for å oppdatere links i redux

            const certurl = findURIWithWindowHostname(
              "rest_certificate",
              orgRegistered.link,
            );

            const certificate = await getJSON<Certificate>(certurl);

            dispatch(certificateFetched(certificate));
            dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));

            context.navigate(`/registrering/${language}/bedrift/i2confirm`);
          } else {
            context.navigate(`/registrering/${language}/bedrift/iconfirm`);
          }
        } else {
          siteImproveEvent({
            category: SiteImproveCategories.ORGANISATION_DONE,
            action: SiteImproveActions.ORGANISATION_DONE_TO_DIGIPOST,
          });
          const registeredOrganisation: RegisteredOrganisation =
            await response.json();
          context.navigate(
            `/registrering/${language}/bedrift/completed?senderId=${registeredOrganisation.registeredSenderId}`,
          );
        }
      }
    } catch (e) {
      dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      logError(e as Error);
    }
  };
}

export function completeIntegrationExistingVirksomhetAction(data: {
  organisationNumber: string;
  integrationPartnerId?: number;
}): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  RegistrationStatusAction | IntegrationPartnerRegisteredAction
> {
  return async (dispatch, getStore) => {
    const { app, link } = getStore();
    const uri = findURIWithWindowHostname("integration_partners", link);
    dispatch(setRegistrationStatus(RegistrationStatus.SUBMITTING));

    try {
      const response = await post(uri, data, app.csrfToken);
      const registeredOrganisation: RegisteredOrganisation =
        await response.json();

      const {
        integrationPartnerRegistered: partnerIsRegistered,
        registeredTechnicalAccountDigipostAddress,
        registeredDigipostAddress,
        registeredTechnicalAccountSenderId,
        registeredSenderId,
        organisationNumber,
      } = registeredOrganisation;

      const address =
        partnerIsRegistered && registeredTechnicalAccountDigipostAddress
          ? registeredTechnicalAccountDigipostAddress
          : registeredDigipostAddress;

      const senderId =
        partnerIsRegistered && registeredTechnicalAccountSenderId
          ? registeredTechnicalAccountSenderId
          : registeredSenderId;

      dispatch(
        integrationPartnerRegistered(
          address,
          senderId,
          organisationNumber,
          true,
          [],
        ),
      );
      dispatch(setRegistrationStatus(RegistrationStatus.COMPLETE));
    } catch (e) {
      dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      logError(e as Error);
      throw e;
    }
  };
}

export type OrgAction =
  | UserInputAction
  | OrganisationsFetchedAction
  | RegistrationStatusAction
  | IntegrationPartnerRegisteredAction
  | CertificateFetchedAction;
